import { USER_ROLE_NAMES, USER_STATUS_NAMES } from "../../../modules/Users/types";
import { UserDto } from "@/common/api/datasources/Api";
import { useUsersQuery } from "@/common/api/services/users";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { enUS, uk } from "date-fns/locale";
import {
    Calendar,
    Clock,
    Mail,
    MessageSquare,
    Phone,
    Shield,
    User,
    UserX
} from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const AVATAR_SIZES = {
    xs: "h-8 w-8",
    sm: "h-10 w-10",
    md: "h-12 w-12",
    lg: "h-16 w-16",
    xl: "h-24 w-24",
} as const;

type AvatarSize = keyof typeof AVATAR_SIZES;

interface UserTooltipProps {
    user?: UserDto;
    userUUID?: string;
    size?: AvatarSize;
    showDetails?: boolean;
    disableNavigation?: boolean;
}

const LoadingTooltip = ({ size = "md" }: { size: AvatarSize }) => {
    return (
        <div className="flex items-center gap-3 p-2">
            <Skeleton className={cn(AVATAR_SIZES[size], "rounded-full")} />
            <div className="space-y-2">
                <Skeleton className="h-4 w-[120px]" />
                <Skeleton className="h-3 w-[160px]" />
            </div>
        </div>
    );
};

const UserTooltipContent = ({
    user,
    size = "md",
    showDetails = true,
    disableNavigation = false
}: {
    user: UserDto;
    size: AvatarSize;
    showDetails?: boolean;
    disableNavigation?: boolean;
}) => {
    const { t, i18n } = useTranslation();
    const dateLocale = i18n.language === "uk" ? uk : enUS;

    const formatDate = (date: string) => {
        return format(new Date(date), "PPp", { locale: dateLocale });
    };

    const fullName =
        user.firstName && user.lastName
            ? `${user.firstName} ${user.lastName}`
            : user.email;
    const initials =
        user.firstName && user.lastName
            ? `${user.firstName[0]}${user.lastName[0]}`
            : t("users.columns.initials");

    const content = (
        <div className="flex items-center gap-3 cursor-pointer hover:bg-muted/50 rounded-md p-2 transition-all duration-200 group">
            <Avatar className={cn(
                AVATAR_SIZES[size],
                "ring-2 ring-offset-2 ring-transparent transition-all duration-200",
                "group-hover:ring-primary/20"
            )}>
                {user.avatarData?.thumbnail?.url ? (
                    <AvatarImage
                        src={user.avatarData.thumbnail.url}
                        alt={fullName}
                        className="object-cover"
                    />
                ) : user.imageGoogleUrl ? (
                    <AvatarImage
                        src={user.imageGoogleUrl}
                        alt={fullName}
                        className="object-cover"
                    />
                ) : null}
                <AvatarFallback className="bg-primary/5 text-primary">
                    {initials}
                </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
                <span className="font-medium group-hover:text-primary transition-colors">
                    {fullName}
                </span>
                <span className="text-sm text-muted-foreground">
                    {user.email}
                </span>
            </div>
        </div>
    );

    return (
        <HoverCard>
            <HoverCardTrigger asChild>
                {disableNavigation ? (
                    content
                ) : (
                    <Link to={`/home/users/${user.userUUID}`}>
                        {content}
                    </Link>
                )}
            </HoverCardTrigger>
            {showDetails && (
                <HoverCardContent className="w-80">
                    <div className="space-y-4">
                        <div className="flex items-start gap-3 pb-3 border-b">
                            <Avatar className={cn(AVATAR_SIZES[size])}>
                                {user.avatarData?.thumbnail?.url ? (
                                    <AvatarImage
                                        src={user.avatarData.thumbnail.url}
                                        alt={fullName}
                                        className="object-cover"
                                    />
                                ) : user.imageGoogleUrl ? (
                                    <AvatarImage
                                        src={user.imageGoogleUrl}
                                        alt={fullName}
                                        className="object-cover"
                                    />
                                ) : null}
                                <AvatarFallback className="bg-primary/5 text-primary text-lg">
                                    {initials}
                                </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col flex-1">
                                <div className="flex items-center justify-between">
                                    <span className="font-semibold text-lg">{fullName}</span>
                                    <button
                                        className="text-muted-foreground hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-muted-foreground"
                                        disabled
                                        title={t("common.userTooltip.chatComingSoon")}
                                    >
                                        <MessageSquare className="w-4 h-4" />
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <span className={cn(
                                        "w-2 h-2 rounded-full",
                                        user.isActive ? "bg-green-500" : "bg-gray-400"
                                    )} />
                                    {user.isActive
                                        ? t("users.status.options.active")
                                        : t("users.status.options.inactive")}
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm">
                                <Mail className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">{t("common.userTooltip.email")}</span>
                                <a
                                    href={`mailto:${user.email}`}
                                    className="ml-auto font-medium hover:text-primary transition-colors"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {user.email}
                                </a>
                            </div>

                            {user.phoneNumber && (
                                <div className="flex items-center gap-2 text-sm">
                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-muted-foreground">{t("common.userTooltip.phone")}</span>
                                    <a
                                        href={`tel:${user.phoneNumber}`}
                                        className="ml-auto font-medium hover:text-primary transition-colors"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {user.phoneNumber}
                                    </a>
                                </div>
                            )}

                            <div className="flex items-center gap-2 text-sm">
                                <User className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">{t("common.userTooltip.role")}</span>
                                <span className="ml-auto font-medium">{t(USER_ROLE_NAMES[user.role])}</span>
                            </div>

                            <div className="flex items-center gap-2 text-sm">
                                <Shield className="w-4 h-4 text-muted-foreground" />
                                <span className="text-muted-foreground">{t("common.userTooltip.status")}</span>
                                <span className="ml-auto font-medium">{t(USER_STATUS_NAMES[user.status])}</span>
                            </div>
                        </div>
                    </div>
                </HoverCardContent>
            )}
        </HoverCard>
    );
};

const NoUserContent = ({ size = "md" }: { size: AvatarSize }) => {
    const { t } = useTranslation();

    return (
        <div className="flex items-center gap-3 p-2">
            <Avatar className={cn(
                AVATAR_SIZES[size],
                "bg-muted"
            )}>
                <AvatarFallback>
                    <UserX className="w-4 h-4 text-muted-foreground" />
                </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
                <span className="text-muted-foreground font-medium">
                    {t("common.userTooltip.noUser")}
                </span>
                <span className="text-sm text-muted-foreground/70">
                    {t("common.userTooltip.userRemoved")}
                </span>
            </div>
        </div>
    );
};

export const UserTooltip = Object.assign(
    ({
        user,
        userUUID,
        size = "md",
        showDetails = true,
        disableNavigation = false
    }: UserTooltipProps) => {
        const usersQuery = useUsersQuery();
        const { data: fetchedUser, isLoading } = useQuery({
            ...usersQuery.userByIdQuery(userUUID || ""),
            enabled: !!userUUID && !user && userUUID !== 'undefined' && userUUID !== 'null'
        });

        if (isLoading) {
            return <LoadingTooltip size={size} />;
        }

        const userData = user || fetchedUser;

        if (!userData && userUUID === 'undefined' || userUUID === 'null') {
            return <NoUserContent size={size} />;
        }

        if (!userData && !userUUID) {
            return <NoUserContent size={size} />;
        }

        if (!userData) {
            return <NoUserContent size={size} />;
        }

        return (
            <UserTooltipContent
                user={userData}
                size={size}
                showDetails={showDetails}
                disableNavigation={disableNavigation}
            />
        );
    },
    {
        size: {
            xs: "xs",
            sm: "sm",
            md: "md",
            lg: "lg",
            xl: "xl"
        } as const
    }
); 